import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

import { FormsModule } from "@angular/forms";
import { HttpResponse } from "@angular/common/http";

import { MaterialModule } from "src/app/app.materials";
import { ReportService } from "src/app/shared/services/report.service";

export interface PdfExportDialogData {
  portfolioId: number;
  portfolioName?: string;
  efficientFrontierData?: any[];
  targetPortfolio?: string;
}

export interface ReportSection {
  label: string;
  component: string;
  selected: boolean;
  group: string;
}

export interface SectionGroup {
  title: string;
  sections: ReportSection[];
}

const ALL_SECTIONS: ReportSection[] = [
  {
    label: "Portfolio Description",
    component: "portfolio-description",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Portfolio Overview",
    component: "portfolio-overview",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Cumulative Return Chart",
    component: "cumulative-return-chart",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Annual Performance Chart",
    component: "annual-performance-chart",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Weights vs Volatility vs Return",
    component: "weights-volatility-return",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Portfolio Constituents Table",
    component: "portfolio-constituents-table",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Portfolio Weights Performance",
    component: "portfolio-weights-performance",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Summary Risk Contribution",
    component: "summary-risk-contribution",
    selected: true,
    group: "Backtest",
  },
  {
    label: "Covariance Correlation Table",
    component: "covariance-correlation-table",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Implied Views",
    component: "implied-views",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Firm House Views",
    component: "firm-house-views",
    selected: true,
    group: "Optimization",
  },
  {
    label: "My Core Views",
    component: "user-core-views",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Portfolio Views (Overrides)",
    component: "my-views",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Effective Views",
    component: "effective-views",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Combined Returns",
    component: "combined-returns",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Constraints",
    component: "constraints",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Efficient Frontier",
    component: "efficient-frontier",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Next Best Trade",
    component: "next-best-trade",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Transition Map",
    component: "transition-map",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Portfolio Weights & Returns",
    component: "optimization-summary",
    selected: true,
    group: "Optimization",
  },
  {
    label: "Risk Expected Risk",
    component: "risk-expected-risk",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Risk Contribution Allocation",
    component: "risk-contribution-allocation",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Marginal Risk Contribution",
    component: "summary-marginal-risk",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Covariance Matrix Table Data",
    component: "covariance-matrix-table-data",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Correlation Matrix Table Data",
    component: "correlation-matrix-table-data",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Hot Spots",
    component: "hot-spots",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Risk Drawdown",
    component: "risk-drawdown",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Risk Volatility",
    component: "risk-volatility",
    selected: true,
    group: "Risk Analysis",
  },
  {
    label: "Best Hedge",
    component: "best-hedge",
    selected: true,
    group: "Risk Analysis",
  },
];

@Component({
  selector: "app-pdf-export-dialog",
  templateUrl: "./pdf-export-dialog.component.html",
  styleUrls: ["./pdf-export-dialog.component.scss"],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MaterialModule],
})
export class PdfExportDialogComponent {
  title = "Portfolio Report";
  subtitle = "";
  theme: "light" | "dark" = "light";

  readonly sections = signal<ReportSection[]>(
    ALL_SECTIONS.map((s) => ({ ...s })),
  );
  readonly orderedSections = signal<ReportSection[]>(
    ALL_SECTIONS.map((s) => ({ ...s })),
  );
  readonly exporting = signal(false);
  readonly error = signal<string | null>(null);

  readonly sectionGroups = computed<SectionGroup[]>(() => {
    const groupMap = new Map<string, ReportSection[]>();
    for (const section of this.sections()) {
      if (!groupMap.has(section.group)) {
        groupMap.set(section.group, []);
      }
      groupMap.get(section.group)!.push(section);
    }
    return Array.from(groupMap.entries()).map(([title, sections]) => ({
      title,
      sections,
    }));
  });

  readonly allSelected = computed(() =>
    this.sections().every((s) => s.selected),
  );
  readonly someSelected = computed(() => {
    const list = this.sections();
    return list.some((s) => s.selected) && !list.every((s) => s.selected);
  });
  readonly canExport = computed(
    () => !this.exporting() && this.orderedSections().length > 0,
  );

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<PdfExportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PdfExportDialogData,
    private reportService: ReportService,
  ) {
    if (data?.portfolioName) {
      this.title = `${data.portfolioName} Report`;
    }
  }

  onSectionToggle(section: ReportSection, checked: boolean): void {
    this.sections.update((list) =>
      list.map((s) =>
        s.component === section.component ? { ...s, selected: checked } : s,
      ),
    );

    if (checked) {
      this.orderedSections.update((list) => {
        if (list.some((s) => s.component === section.component)) {
          return list;
        }
        return [...list, { ...section, selected: true }];
      });
    } else {
      this.orderedSections.update((list) =>
        list.filter((s) => s.component !== section.component),
      );
    }
  }

  onToggleAll(checked: boolean): void {
    this.sections.update((list) =>
      list.map((s) => ({ ...s, selected: checked })),
    );
    this.orderedSections.set(checked ? this.sections() : []);
  }

  onDrop(event: CdkDragDrop<ReportSection[]>): void {
    this.orderedSections.update((list) => {
      const next = [...list];
      moveItemInArray(next, event.previousIndex, event.currentIndex);
      return next;
    });
  }

  onExport(): void {
    if (!this.canExport()) {
      return;
    }

    this.error.set(null);
    this.exporting.set(true);

    const structure = this.orderedSections().map((s, i) => ({
      component: s.component,
      order: i + 1,
    }));

    const payload = {
      portfolio_id: this.data.portfolioId,
      meta: {
        title: this.title,
        subtitle: this.subtitle,
        theme: this.theme,
      },
      options: {
        target_portfolio:
          this.data.targetPortfolio || "efficient_portfolio_max_sharpe_ratio",
        efficient_frontier_data: this.data.efficientFrontierData || [],
      },
      structure,
    };

    this.reportService
      .generateReport(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: HttpResponse<Blob>) =>
          this.handleExportSuccess(response),
        error: (err) => this.handleExportError(err),
      });
  }

  trackByGroup(_: number, group: SectionGroup): string {
    return group.title;
  }

  trackBySection(_: number, section: ReportSection): string {
    return section.component;
  }

  private handleExportSuccess(response: HttpResponse<Blob>): void {
    const blob = response.body;
    if (!blob) {
      this.exporting.set(false);
      this.error.set("Empty response. Please try again.");
      return;
    }

    const filename =
      this.extractFilename(response) || `${this.title || "report"}.pdf`;
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 200);

    this.exporting.set(false);
    this.dialogRef.close(true);
  }

  private handleExportError(err: any): void {
    const blobError: Blob | undefined = err?.error;
    if (blobError instanceof Blob) {
      blobError
        .text()
        .then((text) => this.setErrorFromText(text))
        .finally(() => this.exporting.set(false));
      return;
    }

    this.error.set(
      err?.message || err || "Failed to generate report. Please try again.",
    );
    this.exporting.set(false);
  }

  private setErrorFromText(text: string): void {
    try {
      const parsed = JSON.parse(text);
      this.error.set(parsed?.message || text || "Failed to generate report.");
    } catch {
      this.error.set(text || "Failed to generate report.");
    }
  }

  private extractFilename(response: HttpResponse<Blob>): string | null {
    const disposition = response.headers.get("Content-Disposition");
    if (!disposition) return null;
    const match = disposition.match(/filename="?([^";\n]+)"?/i);
    return match ? match[1].trim() : null;
  }
}
